import { AnimatePresence, motion } from "motion/react";
import type { PlayerView, ReactionEvent, RoomState, RoundResult, Settings } from "@/lib/types";
import { Button } from "@/components/primitives";
import { cn, clock } from "@/lib/utils";

export const REACTIONS = ["🔥", "😭", "💀", "👏", "🧠", "😂"];

/* ------------------------------------------------------------------ people */

function Dot({ player }: { player: PlayerView }) {
  const tone = player.solved
    ? "bg-hit"
    : player.out
      ? "bg-miss"
      : player.connected
        ? "bg-ink-dim"
        : "bg-transparent border border-line";
  return <span className={cn("size-2 shrink-0 rounded-full", tone)} />;
}

/**
 * Who is in the room and, during a round, who is finished. Deliberately says
 * nothing about how many guesses anyone has spent: that lands at round end.
 */
export function Players({
  room,
  me,
}: {
  room: RoomState;
  me: string | null;
}) {
  const playing = room.phase === "playing";
  const order = [...room.players].sort((a, b) => b.score - a.score);

  return (
    <div className="panel flex flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-dim">
          Players
        </h2>
        {/* The cap is 20 and the server refuses the twenty-first. */}
        <span className="text-[11px] font-bold text-ink-faint tabular-nums">
          {room.players.length}/20
        </span>
      </div>

      <ul className="flex max-h-[240px] flex-col overflow-y-auto lg:max-h-none">
        {order.length === 0 && (
          <li className="px-3 py-4 text-[13px] text-ink-faint">Nobody has joined yet.</li>
        )}
        {order.map((player) => (
          <li
            key={player.username}
            className={cn(
              "flex items-center gap-2.5 border-b border-line/60 px-3 py-2.5 last:border-b-0",
              !player.connected && "opacity-45",
            )}
          >
            <Dot player={player} />
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-[14px]",
                player.username === me && "font-bold",
              )}
            >
              {player.username}
              {player.username === me && <span className="text-ink-faint"> (you)</span>}
            </span>
            {playing && player.solved && (
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-hit">
                Solved
              </span>
            )}
            {playing && player.out && (
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-faint">
                Out
              </span>
            )}
            <span className="w-10 text-right text-[13px] font-bold tabular-nums">
              {player.score}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------- host panel */

export function HostPanel({
  room,
  onSettings,
  onStart,
  onEndRound,
  onHint,
  onEndGame,
  onNewGame,
}: {
  room: RoomState;
  onSettings: (patch: Partial<Settings>) => void;
  onStart: () => void;
  onEndRound: () => void;
  onHint: (kind: "category" | "definition") => void;
  onEndGame: () => void;
  onNewGame: () => void;
}) {
  const playing = room.phase === "playing";
  const lastRoundPlayed = room.round >= room.settings.totalRounds;

  return (
    <div className="panel flex flex-col gap-4 p-3.5">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-dim">
          Host controls
        </h2>
        <span className="text-[11px] font-bold text-ink-faint">
          Round {room.round}/{room.settings.totalRounds}
        </span>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="flex items-center justify-between text-[12px] text-ink-dim">
          Round time
          <span className="font-bold text-ink tabular-nums">
            {clock(room.settings.roundSeconds)}
          </span>
        </span>
        <input
          type="range"
          min={60}
          max={300}
          step={10}
          value={room.settings.roundSeconds}
          onChange={(e) => onSettings({ roundSeconds: Number(e.target.value) })}
          className="accent-hit"
        />
        {playing && <span className="text-[11px] text-ink-faint">Applies to the next round</span>}
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="flex items-center justify-between text-[12px] text-ink-dim">
          Rounds at most
          <span className="font-bold text-ink tabular-nums">{room.settings.totalRounds}</span>
        </span>
        <input
          type="range"
          min={1}
          max={30}
          step={1}
          value={room.settings.totalRounds}
          onChange={(e) => onSettings({ totalRounds: Number(e.target.value) })}
          className="accent-hit"
        />
        <span className="text-[11px] text-ink-faint">
          A ceiling, not a promise. End the game whenever you like.
        </span>
      </label>

      <label className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[13px]">Hard mode</span>
          <span className="text-[11px] text-ink-faint">Revealed hints must be reused</span>
        </span>
        <input
          type="checkbox"
          checked={room.settings.hardMode}
          onChange={(e) => onSettings({ hardMode: e.target.checked })}
          className="size-5 accent-hit"
        />
      </label>

      <div className="flex flex-col gap-2 border-t border-line pt-3.5">
        {!playing && !room.finished && (
          <Button onClick={onStart} disabled={room.players.length === 0}>
            {room.round === 0 ? "Start round 1" : `Start round ${room.round + 1}`}
          </Button>
        )}

        {playing && (
          <>
            <Button
              variant="ghost"
              className="h-auto min-h-11 py-2 text-left leading-snug whitespace-normal"
              onClick={() => onHint("category")}
              disabled={room.hintUsed}
            >
              {room.hintUsed ? `Category out: ${room.categoryHint}` : "Give category hint"}
            </Button>
            {/* The heavier of the two. Deliberately second, and worded so a
                host knows what they are about to hand over. */}
            <Button
              variant="ghost"
              className="h-auto min-h-11 py-2 text-left leading-snug whitespace-normal"
              onClick={() => onHint("definition")}
              disabled={room.definitionUsed}
            >
              {room.definitionUsed
                ? `Definition out: ${room.definitionHint}`
                : "Give definition hint"}
            </Button>
            <Button variant="ghost" onClick={onEndRound}>
              End round now
            </Button>
          </>
        )}

        {/* Always available: the host decides when the evening is over. */}
        {!room.finished && room.round > 0 && (
          <Button variant="ghost" onClick={onEndGame}>
            {playing ? "Score this round and end" : "End game"}
          </Button>
        )}

        {(room.finished || lastRoundPlayed) && (
          <Button variant="ghost" onClick={onNewGame}>
            New game
          </Button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- round clock */

export function RoundBar({ room }: { room: RoomState }) {
  const playing = room.phase === "playing";
  const fraction = playing ? room.secondsLeft / room.settings.roundSeconds : 0;
  const low = room.secondsLeft <= 10 && playing;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 text-[12px] uppercase tracking-[0.12em] text-ink-dim">
        <span className="shrink-0 font-bold">
          Round {Math.max(room.round, 1)} / {room.settings.totalRounds}
        </span>
        <span className="flex min-w-0 items-center gap-3">
          {room.settings.hardMode && <span className="shrink-0 text-near">Hard</span>}
          {room.categoryHint && (
            <span className="min-w-0 truncate text-ink">{room.categoryHint}</span>
          )}
          <span className={cn("shrink-0 font-bold tabular-nums", low ? "text-near" : "text-ink")}>
            {clock(room.secondsLeft)}
          </span>
        </span>
      </div>
      <div className="h-[3px] w-full overflow-hidden rounded-full bg-line">
        <motion.div
          className={cn("h-full", low ? "bg-near" : "bg-hit")}
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%` }}
          transition={{ duration: 0.9, ease: "linear" }}
        />
      </div>

      {/* A whole sentence: it gets its own line rather than fighting the clock
          for room in the strip above. */}
      <AnimatePresence initial={false}>
        {room.definitionHint && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden text-center text-[13px] leading-snug text-balance text-ink-dim"
          >
            {room.definitionHint}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

/* --------------------------------------------------------------- reactions */

export function ReactionBar({ onReact }: { onReact: (emoji: string) => void }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {REACTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onReact(emoji)}
          className="flex size-9 items-center justify-center rounded-full border border-line text-[17px] transition-colors hover:border-line-bright hover:bg-bg-raised"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

/** Reactions float up the right edge, tagged with who sent them. */
export function ReactionStream({ reactions }: { reactions: ReactionEvent[] }) {
  return (
    <div className="pointer-events-none fixed bottom-24 right-4 z-40 flex w-40 flex-col items-end gap-1">
      <AnimatePresence>
        {reactions.map((reaction) => (
          <motion.div
            key={reaction.id}
            className="float-up flex items-center gap-1.5"
            style={{ animationDuration: "1600ms" }}
          >
            <span className="text-[11px] font-bold text-ink-dim">{reaction.username}</span>
            <span className="text-[24px]">{reaction.emoji}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/* -------------------------------------------------------------- round end */

export function RoundEnd({
  room,
  onClose,
}: {
  room: RoomState;
  onClose: () => void;
}) {
  const results: RoundResult[] = room.results;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg/85 px-4 py-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
        /* Capped to the visible window, so on a short phone the button at the
           bottom is reachable instead of sitting off the end of the card. */
        className="panel flex max-h-[calc(100dvh-2rem)] w-full max-w-[440px] flex-col overflow-hidden"
      >
        <div className="flex shrink-0 flex-col items-center gap-2 border-b border-line px-5 py-6 text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-dim">
            {room.finished ? "Final word" : `Round ${room.round} of ${room.settings.totalRounds}`}
          </p>
          <p className="wordmark text-[clamp(22px,7vw,30px)] break-all text-hit">{room.lastWord}</p>
          {room.lastCategory && (
            <p className="text-[12px] uppercase tracking-[0.12em] text-ink-faint">
              {room.lastCategory}
            </p>
          )}
          {/* Everybody sees what it meant, whether or not the host gave it out. */}
          {room.lastDefinition && (
            <p className="max-w-[40ch] text-[13px] leading-snug text-balance text-ink-dim">
              {room.lastDefinition}
            </p>
          )}
        </div>

        <ul className="min-h-0 flex-1 overflow-y-auto">
          {results.map((result) => (
            <li
              key={result.username}
              className="flex items-center gap-2 border-b border-line/60 px-4 py-3 last:border-b-0 sm:gap-3 sm:px-5"
            >
              <span className="min-w-0 flex-1 truncate text-[14px]">{result.username}</span>
              <span
                className={cn(
                  "w-[86px] shrink-0 text-right text-[12px] tabular-nums sm:w-24",
                  result.solved ? "text-ink-dim" : "text-ink-faint",
                )}
              >
                {result.solved
                  ? `${result.tries} ${result.tries === 1 ? "try" : "tries"} · ${result.seconds}s`
                  : "did not solve"}
              </span>
              <span
                className={cn(
                  "w-11 shrink-0 text-right text-[14px] font-bold tabular-nums sm:w-12",
                  result.points > 0 ? "text-hit" : "text-ink-faint",
                )}
              >
                +{result.points}
              </span>
            </li>
          ))}
        </ul>

        <div className="shrink-0 border-t border-line px-5 py-4">
          <Button variant="ghost" className="w-full" onClick={onClose}>
            {room.finished ? "See the podium" : "Back to the room"}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ----------------------------------------------------------------- podium */

export function Podium({ room }: { room: RoomState }) {
  const [first, second, third] = room.standings;
  const rest = room.standings.slice(3);

  return (
    <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-8 overflow-y-auto py-8">
      <div className="px-2 text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-dim">
          Game over
        </p>
        <h2 className="wordmark mt-2 text-[clamp(21px,6.5vw,30px)] break-words">
          {first ? first.username : "Nobody"} wins
        </h2>
      </div>

      <div className="flex w-full items-end justify-center gap-2">
        {[second, first, third].map((standing, i) => {
          // Empty steps still hold their place, so the winner stays centred.
          // Each step is a third of the row on a phone rather than a fixed
          // 110px, which is what used to push the podium off a 320px screen.
          if (!standing) return <div key={i} className="w-full max-w-[110px] flex-1" />;
          const heights = ["h-20", "h-28", "h-16"];
          const tones = ["bg-line", "bg-hit", "bg-near"];
          return (
            <div
              key={standing.username}
              className="flex w-full max-w-[110px] min-w-0 flex-1 flex-col items-center gap-2"
            >
              <span className="max-w-full truncate text-[13px] font-bold">{standing.username}</span>
              <span className="text-[12px] tabular-nums text-ink-dim">{standing.score}</span>
              <div
                className={cn(
                  "flex w-full items-start justify-center rounded-t-[4px] pt-2",
                  heights[i],
                  tones[i],
                )}
              >
                <span className="wordmark text-[18px]">{standing.rank}</span>
              </div>
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <ul className="panel w-full overflow-hidden">
          {rest.map((standing) => (
            <li
              key={standing.username}
              className="flex items-center gap-3 border-b border-line/60 px-4 py-2.5 last:border-b-0"
            >
              <span className="w-6 text-[13px] text-ink-faint tabular-nums">{standing.rank}</span>
              <span className="min-w-0 flex-1 truncate text-[14px]">{standing.username}</span>
              <span className="text-[14px] font-bold tabular-nums">{standing.score}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
